import { Box, Flex, Text } from "@chakra-ui/react";
import { Modal } from "antd";
import FooterModal from "components/Button/FooterModal";
import IconShadowSelectableButton from "components/Button/IconShadowSelectableButton";
import { ModalLiquidarContas } from "components/Features/Financeiro/ContentConta/components/ModalLiquidarContas";
import ModalFormPagarConta from "components/Features/Financeiro/FormConta/ModalFormPagarConta";
import Field from "components/Input/Field";
import ModalHistoricoConta from "components/ModalHistoryConta";
import { TransferenciaItem } from "core/features/transferencia/typings";
import { OperacaoEnum } from "core/resources/enums/conta";
import colors from "core/resources/theme/colors";
import useConciliarExtrato from "features/conciliacao-bancaria/hooks/useConciliarExtrato";
import { useContaStore } from "features/conta/store";
import { ContaFiltroType, ContaItemResponse } from "features/conta/typings";
import { showSuccess } from "infra/helpers/alerta";
import { formatMoney } from "infra/helpers/number";
import moment from "moment";
import { useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { BiTransfer } from "react-icons/bi";
import { MdOutlineAttachMoney } from "react-icons/md";
import ListaContas from "./components/ListaContas";
import ListaTransferencias from "./components/ListaTransferencias";
import { useInvalidateQuery } from "infra/packages/react-query";
import { ContaParaExtratoBancarioResponse } from "features/conciliacao-bancaria/typings";

interface ModalConciliarProps {
  visible: boolean;
  valor: number | undefined;
  onClose?: () => any;
  operacao: OperacaoEnum;
  extratoId: string;
  extratoData: string;
  mes: string;
  refetch?: () => any;
  tenantContaIdExtrato: string;
  conciliado?: boolean | undefined;
  contasBancariasIds?: string[];
}

interface ModalConciliarForm {
  tipoConciliacao: "conta" | "transferencia";
  contas?: string[];
  transferenciaId?: string;
  transferencia?: TransferenciaItem;
  extratoId: string;
}

export default function ModalConciliar({
  visible,
  onClose,
  operacao,
  extratoId,
  extratoData,
  tenantContaIdExtrato,
  mes,
  valor,
  refetch,
  conciliado,
  contasBancariasIds,
}: ModalConciliarProps) {
  const { doInvalidateQueryWithStringKey } = useInvalidateQuery();

  const { reset: resetFiltersConta, filters, update } = useContaStore();
  const initialFiltersRef = useRef(filters);

  const [selecteds, setSelecteds] = useState<
    ContaParaExtratoBancarioResponse[]
  >([]);
  const [historyId, setHistoryId] = useState<string | undefined>(undefined);
  const [pagarContaId, setPagarContaId] = useState<string | undefined>(
    undefined
  );
  const [liquidarContasData, setLiquidarContasData] = useState<
    ContaItemResponse[]
  >([]);

  const { mutate: conciliar, isLoading: isLoadingConciliar } =
    useConciliarExtrato();

  useEffect(() => {
    let next: Partial<ContaFiltroType> = {};

    if (conciliado) {
      next = {
        dataInicio: moment(extratoData)
          .subtract(1, "month")
          .format("YYYY-MM-DD"),
        dataFim: moment(extratoData).add(1, "month").format("YYYY-MM-DD"),
        operacao,
        naoConciliado: undefined,
        extratosBancariosIds: [extratoId],
        tipoData: "movimentacao",
        orderBy: "c.dataMovimentacao",
        limit: 10,
        orderDirection: "ASC",
      };
    } else {
      next = {
        pago: true,
        operacao,
        naoConciliado: "1",
        dataInicio: moment(extratoData).startOf("day").format("YYYY-MM-DD"),
        dataFim: moment(extratoData).endOf("day").format("YYYY-MM-DD"),
        tipoData: "movimentacao",
        orderBy: "c.dataMovimentacao",
        orderDirection: "ASC",
        limit: 10,
        contasBancariasIds,
      };
    }

    resetFiltersConta(next);

    const _initialFilters = initialFiltersRef.current;
    return () => {
      resetFiltersConta(_initialFilters);
    };
  }, [
    conciliado,
    contasBancariasIds,
    extratoData,
    extratoId,
    mes,
    operacao,
    resetFiltersConta,
  ]);

  const { control, setValue, getValues, reset, watch, handleSubmit } =
    useForm<ModalConciliarForm>({
      defaultValues: {
        extratoId,
        tipoConciliacao: "conta",
      },
      mode: "all",
    });

  const form = watch();

  useEffect(() => {
    if (!visible) {
      const form = getValues();

      reset({
        extratoId: form.extratoId,
        tipoConciliacao: "conta",
        contas: [],
        transferenciaId: undefined,
      });
    }
  }, [getValues, reset, visible]);

  useEffect(() => {
    setValue("contas", []);
    setValue("transferenciaId", undefined);
    setValue("transferencia", undefined);
    update({
      tipoData: "movimentacao",
    });
  }, [setValue, update]);

  const total = useMemo(() => {
    if (!selecteds.length && !form.transferencia) {
      return 0;
    }

    let valor = 0;

    valor +=
      selecteds.reduce((agg, item) => agg + item.valorLiquido * operacao, 0) ??
      0;

    valor += operacao * (form.transferencia?.valor ?? 0);

    return valor;
  }, [form.transferencia, operacao, selecteds]);

  const onSubmit = (data: ModalConciliarForm) => {
    conciliar(
      {
        extratoId,
        contas: selecteds.map((item) => item.id),
        transferenciaId: data.transferencia?.id ?? data.transferenciaId,
      },
      {
        onSuccess: async () => {
          await showSuccess();
          await refetch?.();
          onClose?.();
        },
      }
    );
  };

  return (
    <>
      <Modal
        centered
        visible={visible}
        footer={
          <FooterModal
            isLoadingSave={isLoadingConciliar}
            showRegisterAnother={false}
            hidePrimaryButton={conciliado ? true : false}
            primaryButtonText={"Conciliar"}
            buttonCancelarText="Voltar"
            onClose={onClose}
            isDisabled={
              (!selecteds.length && !form.transferenciaId) ||
              filters.tipoData === "vencimento"
            }
            onSubmit={handleSubmit(onSubmit)}
            right={
              <Flex flexDir="column" flex={1} alignItems="flex-start">
                {!conciliado &&
                valor &&
                total &&
                Math.abs(total - valor) > 0.01 ? (
                  <Text color={colors.red500}>
                    (Valor selecionado{" "}
                    {total - valor > 0.01 ? "ultrapassa" : "abaixo do"} valor do
                    extrato)
                  </Text>
                ) : null}
                <Flex flex={1} fontWeight="bold" fontSize="16px" gap="16px">
                  <Flex>Valor do extrato: {formatMoney(valor)}</Flex>
                  {!conciliado ? (
                    <>
                      {" "}
                      <Flex>/</Flex>
                      <Flex>
                        Total selecionado: {formatMoney(total ?? 0)}
                      </Flex>{" "}
                    </>
                  ) : null}
                </Flex>
              </Flex>
            }
          />
        }
        width="1500px"
        onCancel={onClose}
      >
        <Flex flexDir="column" mb={-8}>
          <Flex gap={4} alignItems="center" mb={4}>
            <Text as="h3" fontSize={20} fontWeight="500">
              {conciliado ? "Conciliado" : "Conciliar"}
            </Text>
          </Flex>
          {!conciliado ? (
            <Field
              name="tipoConciliacao"
              rules={{ required: true }}
              control={control}
              render={({ field }) => (
                <Flex
                  alignItems="center"
                  pb={2}
                  justifyContent="center"
                  gridGap={10}
                >
                  <IconShadowSelectableButton
                    selected={field.value === "conta"}
                    text={"Conciliar com uma conta"}
                    badge={selecteds.length}
                    fontSize={14}
                    Icon={MdOutlineAttachMoney}
                    value={"conta"}
                    onChange={field.onChange}
                    color={colors.primary}
                    iconSize={14}
                    buttonProps={{
                      maxW: "35%",
                      h: "80px",
                    }}
                  />

                  <IconShadowSelectableButton
                    selected={field.value === "transferencia"}
                    text={"Conciliar com uma transferência"}
                    badge={
                      form.transferenciaId || form.transferencia?.id
                        ? 1
                        : undefined
                    }
                    fontSize={14}
                    Icon={BiTransfer}
                    value={"transferencia"}
                    onChange={field.onChange}
                    color={colors.primary}
                    iconSize={14}
                    buttonProps={{
                      maxW: "35%",
                      h: "80px",
                    }}
                  />
                </Flex>
              )}
            />
          ) : null}
          <Box maxH="600px" overflow="hidden">
            {form.tipoConciliacao === "conta" ? (
              <ListaContas
                maxH="550px"
                minH="300px"
                defaultSelectedRows={selecteds}
                setValue={setValue}
                setSelecteds={setSelecteds}
                selectRowVisible={conciliado ? false : true}
                extratoId={extratoId}
                extratoData={extratoData}
                operacao={operacao}
                conciliado={conciliado}
                contasBancariasIds={contasBancariasIds}
                setHistoryId={setHistoryId}
                setPagarContaId={setPagarContaId}
                setLiquidarContasData={setLiquidarContasData}
              />
            ) : null}
            {form.tipoConciliacao === "transferencia" ? (
              <ListaTransferencias
                selectedTransferenciaId={
                  form.transferencia?.id ?? form.transferenciaId
                }
                setValue={setValue}
                tenantContaIdExtrato={tenantContaIdExtrato}
                valor={valor}
              />
            ) : null}
          </Box>
        </Flex>
      </Modal>

      {historyId ? (
        <ModalHistoricoConta
          isOpenModal={!!historyId}
          onClose={() => {
            setHistoryId(undefined);
          }}
          contaId={historyId}
        />
      ) : null}

      {pagarContaId ? (
        <ModalFormPagarConta
          operacao={filters.operacao!}
          contaId={pagarContaId}
          isOpen={!!pagarContaId}
          onClose={() => setPagarContaId(undefined)}
          refetch={() => {
            setPagarContaId(undefined);
            doInvalidateQueryWithStringKey("/v1/extrato-bancario/contas");
            refetch?.();
          }}
          dataPagamentoPadraoIgualADataVencimento
        />
      ) : null}

      {liquidarContasData?.length ? (
        <ModalLiquidarContas
          isOpenModal={!!liquidarContasData.length}
          data={liquidarContasData}
          refetch={() => {
            setSelecteds([]);
            doInvalidateQueryWithStringKey("/v1/extrato-bancario/contas");
            refetch?.();
          }}
          operacao={filters.operacao}
          onClose={() => setLiquidarContasData([])}
        />
      ) : null}
    </>
  );
}
